import {
  type Dispatch,
  type SetStateAction,
  memo,
  useCallback,
  useMemo,
  useState,
} from "react";
import Form from "../../../../components/Form/Form";
import { H2, P } from "../../../../components/Text/Text";
import { ValidatorsE } from "../../../../components/Form/yupValidations";
import Button, { ButtonModeE } from "../../../../components/Button/Button";
import { useRegisterUserMutation } from "../../../../api/userAPI";
import { AccessLevelE, UserStatusE } from "../../../../models/enums";
import generatePassword from "../../../../popups/CreateEditUser/helpers/generatePassword";
import CopyIcon from "../../../../popups/CreateEditUser/components/CopyIcon/CopyIcon";
import { useParams } from "react-router";

import styles from "./styles.module.scss";

import type { ClientT, FormFieldT, UserT } from "../../../../models/models";
import type { GetPredefinedUserDataResultT } from "../../../../api/models/models";

interface SecondStepI {
  clientData: GetPredefinedUserDataResultT;
  client: Partial<ClientT>;
  setIsRegistrationCompleted: Dispatch<SetStateAction<boolean>>;
  setCreatedUserData: Dispatch<
    SetStateAction<Partial<UserT> & { Password: string }>
  >;
}

const FORM_ID = "registration-second-step-form";

const SecondStep = memo((props: SecondStepI) => {
  const { clientData, client, setIsRegistrationCompleted, setCreatedUserData } =
    props;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [registerUser] = useRegisterUserMutation();
  const [password, setPassword] = useState("");
  const { token } = useParams();

  const handleGeneratePassword = () => {
    const generatedPassword = generatePassword();
    setPassword(generatePassword);
    const updatePasswordField = new CustomEvent("updateFields", {
      detail: [{ name: "Password", value: generatedPassword }],
    });
    document.dispatchEvent(updatePasswordField);
  };

  const fields = useMemo(
    () =>
      [
        {
          label: "First name",
          name: "FirstName",
          validation: ValidatorsE.REQUIRED_STRING,
        },
        {
          label: "Last name",
          name: "LastName",
          validation: ValidatorsE.REQUIRED_STRING,
        },
        {
          label: "User name",
          name: "UserName",
          validation: ValidatorsE.REQUIRED_STRING,
        },
        {
          label: "Password",
          name: "Password",
          defaultValue: password,
          validation: password?.length ? ValidatorsE.PASSWORD : ValidatorsE.REQUIRED_STRING,
          onChange: (value: string) => {
            setPassword(value);
          },
          extraContent: () => (
            <>
              <Button
                onClick={handleGeneratePassword}
                className={styles.generatePasswordButton}
                mode={ButtonModeE.LINK}
              >
                Generate password
              </Button>
              {!!password && <CopyIcon inputName="Password" />}
            </>
          ),
        },
        {
          label: "Email",
          name: "Email",
          validation: ValidatorsE.EMAIL,
          defaultValue: clientData.UserEmail,
        },
        {
          label: "Phone",
          name: "PhoneNumber",
          validation: ValidatorsE.PHONE_NUMBER,
          defaultValue: clientData.UserPhoneNumber,
        },
        { label: "Job title", name: "JobTitle" },
      ] as FormFieldT[],
    [clientData.UserEmail, clientData.UserPhoneNumber, password]
  );

  const handleSubmit = useCallback(
    async (data: any) => {
      setIsSubmitting(true);

      try {
        const payload = {
          ...data,
          AccessLevel: clientData.IsDeliveryService
            ? AccessLevelE.DeliveryServicePrivate
            : AccessLevelE.Admin,
          ClientId: client?.Id,
          Culture: clientData.Language,
          Status: UserStatusE.Enabled,
          DisabledNotificationEmails: true,
          OnlyMyProjects: false,
          Token: decodeURIComponent(token),
        };

        const res = await registerUser(payload);

        if ("data" in res) {
          setIsRegistrationCompleted(true);
          setCreatedUserData({ ...res.data, Password: data.Password });
        }
      } catch (e) {
        console.log(e);
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      client?.Id,
      clientData?.IsDeliveryService,
      clientData?.Language,
      registerUser,
      setCreatedUserData,
      setIsRegistrationCompleted,
    ]
  );

  return (
    <div className={styles.wrapper}>
      <H2>Second step - user data</H2>
      <P>Setting up your user account information</P>
      <hr />
      <div className={isSubmitting ? styles.disabledForm : ""}>
        <Form fields={fields} formID={FORM_ID} onSubmit={handleSubmit} />
      </div>
      <div className={styles.footer}>
        <Button form={FORM_ID} disabled={isSubmitting} loading={isSubmitting}>
          Create user
        </Button>
      </div>
    </div>
  );
});

export default SecondStep;
